import dayjs from 'dayjs'
import _ from 'lodash'
import { createSelector } from 'reselect'
import {
  getLatestRoundTime,
  isRoundOverdue
} from '../../features/reports/utils/medicationRoundsTableUtils'
import {
  getEMARResidentMeds,
  getEMARRounds,
  getEMARRoundTimings,
  getLogsList,
  getResidentsList,
  getUsersList
} from '../Lists'
import { populateLogs } from '../Logs'
import {
  getNonPRNMedication,
  getPRNMedication,
  getRoundResidents
} from './utils'

const getResidentId = (_state, props) => _.get(props, 'router.params.id')
const getMedicationId = (_state, props) =>
  _.get(props, 'router.params.medication_id')

// getResidentMarChart example output:
// const result = [
//     {
//         residentMed: '00',
//         name: 'Aspirin 300mg dispersible tablets',
//         desc: 'text',
//         instructions: 'Lorem ipsum, or lipsum as it is sometimes known, is dummy text used in laying out print, graphic or web designs.',
//         roundTimings: [
//             {
//                 time: '08:00',
//                 dose: 1,
//                 emarRoundTiming: 'ID',
//                 rounds: [
//                     {
//                         timeLogged: '2020-11-10T00:00:00.000Z',
//                         loggedBy: 'ID',
//                         taken: false,
//                         notTakenCode: 'A',
//                         note: 'Ted didn\'t want his meds',
//                     },
//                     {
//                         timeLogged: '2020-10-10T00:00:00.000Z',
//                         loggedBy: 'ID',
//                         taken: true,
//                         note: 'Ted took his meds',
//                     }
//                 ]
//             }
//         ]
//     }
// ]

// Used to filter logs for the log timelines in the Medication Stock & Details screens
const getProps = (_state, props) => props
export const getMedicationLogs = createSelector(
  [getProps, populateLogs],
  (props, allLogs) => {
    return _(allLogs)
      .filter({
        ...(props && props.id && { emarResidentMedication: props.id })
      })
      .map((log) => ({
        ...log,
        actualStock: log.amount1,
        expectedStock: log.amount2
      }))
      .value()
  }
)

export const getDiscrepancyLogs = createSelector(
  [getMedicationLogs, getEMARResidentMeds],
  (logs, emarResidentMeds) => {
    return _(logs)
      .filter(({ emarResidentMedication, actualStock, expectedStock }) => {
        return (
          emarResidentMedication &&
          _.isNumber(actualStock) &&
          _.isNumber(expectedStock) &&
          actualStock !== expectedStock
        )
      })
      .map((log) => ({
        ...log,
        emarResidentMedication: _.find(emarResidentMeds, {
          id: log.emarResidentMedication
        })
      }))
      .value()
  }
)

export const getResidentStockCount = createSelector(
  [getProps, getEMARResidentMeds, getUsersList],
  ({ resident }, emarResidentMeds, carers) => {
    if (!emarResidentMeds || !carers) return null
    return _(emarResidentMeds)
      .filter({
        deleted: false,
        resident
      })
      .orderBy('medication.name')
      .map((med) => {
        if (!med.stockLog) return med
        return {
          ...med,
          stockLog: {
            ...med.stockLog,
            actualStock: med.stockLog.amount1,
            expectedStock: med.stockLog.amount2,
            loggedBy: _.find(carers, { id: med.stockLog?.loggedBy }),
            witnessedBy: _.find(carers, { id: med.stockLog.witnessedBy })
          }
        }
      })
      .value()
  }
)

export const getResidentMarChart = createSelector(
  [
    getResidentId,
    getEMARRounds,
    getEMARRoundTimings,
    getEMARResidentMeds,
    getUsersList
  ],
  (resident, emarRounds = [], emarRoundTimings, emarResidentMeds, carers) => {
    if (!resident || !emarRoundTimings || !emarResidentMeds || !carers)
      return null

    return (
      _(emarResidentMeds)
        .filter({ resident })
        // TODO: filter meds by date / active / paused etc.
        .map((medication) => {
          return medication.PRN
            ? getPRNMedication({
                medication,
                resident,
                emarRounds,
                carers
              })
            : getNonPRNMedication({
                medication,
                resident,
                emarRounds,
                emarRoundTimings,
                carers
              })
        })
        .value()
    )
  }
)

export const getResidentMarChartUpdated = createSelector(
  [
    getResidentId,
    getEMARRounds,
    getEMARRoundTimings,
    getEMARResidentMeds,
    getUsersList
  ],
  (resident, emarRounds, emarRoundTimings, emarResidentMeds, carers) => {
    if (
      !(
        resident &&
        emarRoundTimings &&
        emarResidentMeds &&
        carers &&
        emarRounds
      )
    )
      return []

    return (
      _(emarResidentMeds)
        .filter({ resident })
        // TODO: filter meds by date / active / paused etc.
        .map((medication) => {
          return medication.PRN
            ? getPRNMedication({
                medication,
                resident,
                emarRounds,
                carers
              })
            : getNonPRNMedication({
                medication,
                resident,
                emarRounds,
                emarRoundTimings,
                carers
              })
        })
        .value()
    )
  }
)

// export const getStockLevelTableData = createSelector(
//     [getEMARResidentMeds, getResidentsList],
//     (emarResidentMeds, residents) => {
//         return _(emarResidentMeds)
//             // .filter(({ emarResidentMedication, actualStock, expectedStock }) => {
//             //     return emarResidentMedication && _.isNumber(actualStock) && _.isNumber(expectedStock) && actualStock !== expectedStock
//             // })
//             .map(med => {
//                 const currentStock = med.stockLog && (med.stockLog.amount1 || med.stockLog.amount2)
//                 return {
//                     emarResidentMedication: med,
//                     resident: _.find(residents, { id: med.resident }),
//                     stockLevelSortValue: currentStock,
//                     stockLevel: {
//                         stock: currentStock,
//                         stockLog: med.stockLog,
//                         stockQuantityType: med.stockQuantityType,
//                         stockAlertLevel: med.stockAlertLevel,
//                     }
//                 }
//             })
//             .value()
//     }
// )

// Get stock level alerts for the Stock Alert report
// Uses the medication stockLog to get all medications currently low on stock
const ACTIVE_STATUSES = ['active', 'pre-assessment', 'dummy']

const getResidentStockEntries = (emarResidentMeds, residents) => {
  if (!residents) return []
  return residents.map((resident) => {
    const meds = _.chain(emarResidentMeds)
      .filter({ resident: resident.id })
      .filter((med) => {
        if (!med.stockLog) return false
        if (!med.stockAlertLevel) return false
        if (med.deleted) return false
        if (med.endDate && dayjs(med.endDate).isBefore(dayjs())) return false
        const currentStock = _.isNumber(med.stockLog.amount1)
          ? med.stockLog.amount1
          : med.stockLog.amount2

        if (!_.isNumber(currentStock)) return false
        if (currentStock > med.stockAlertLevel) return false

        return true
      })
      .orderBy((med) => med.stockLog.amount1 || med.stockLog.amount2)
      .value()

    return { resident, emarResidentMeds: meds }
  })
}

export const getStockLevelReportGrouped = createSelector(
  [getEMARResidentMeds, getResidentsList],
  (emarResidentMeds, residents) => {
    const entries = getResidentStockEntries(emarResidentMeds, residents)
    const activeResidents = entries.filter(({ resident }) =>
      ACTIVE_STATUSES.includes(resident.status)
    )
    const nonActiveResidents = entries.filter(
      ({ resident }) => !ACTIVE_STATUSES.includes(resident.status)
    )
    return { activeResidents, nonActiveResidents }
  }
)

export const getMedicationMarChart = createSelector(
  [getMedicationId, getResidentMarChart, getUsersList],
  (id, medicationMarCharts, carers) => {
    if (!carers || !medicationMarCharts) return null
    const chart = _.find(medicationMarCharts, { id })

    // populate carers
    if (chart && chart.stockLog) {
      return {
        ...chart,
        stockLog: {
          ...chart.stockLog,
          loggedBy: _.find(carers, { id: chart.stockLog?.loggedBy }),
          witnessedBy: _.find(carers, { id: chart.stockLog.witnessedBy })
        }
      }
    }

    return chart
  }
)
// Selector for Rounds Report Screen

/*
API Report format

input: [
    // All signed
    {
        time: '08:00',
        scheduled: {
            due: 3,
            signed: 2,
            notAdministered: 1,
        },
        PRN: 1,
        residents: [
            {
                loggedBy: 'ID',
                witnessedBy: 'ID',
                signed: true / false,
                resident: 'ID',
                additionalComment: 'text',
                meds: [
                    {
                        reasonCode: 'text',
                        medicationName: 'text',
                        note: 'text',
                        logId: 'ID',
                    },
                    {
                        medicationName: 'text',
                        note: 'text',
                        logId: 'ID',
                        PRN: true / false
                    },
                ]
            }
        ]
    },
]

result:
    {
        time: '08:00',
        scheduled: {
            due: 3,
            signed: 2,
            notAdministered: 1,
        },
        PRN: 1,
        residents: [
            {
                loggedBy: { name: { first: 'Test', last: 'Carer' } },
                witnessedBy: { name: { first: 'Witness', last: 'This' } },
                signed: true / false,
                resident: { name: { first: 'Mathieu', last: 'Williams' }, picture: null }
                additionalComment: 'text',
                meds: [
                    {
                        taken: true / false
                        reasonCode: 'text',
                        medicationName: 'text',
                        note: 'text',
                        PRN: true / false,
                        logId: 'ID',
                    },
                    {
                        medicationName: 'text',
                        note: 'text',
                        logId: 'ID',
                        PRN: true / false
                    },
                ]
            }
        ]
    },
*/

const getEMARRoundsReport = (_state, { requestID }) =>
  _.get(_state, `data.requests.${requestID}.response`)

const getResidentSortType = (state) => state.global.home.sortByRoomNumber

export const getMedicationRoundsReportTable = createSelector(
  [
    getEMARRoundsReport,
    getResidentsList,
    getResidentSortType,
    getUsersList,
    getLogsList
  ],
  (roundsReport, residentsDetails, residentSortByRoomNumber, carers, logs) => {
    if (!roundsReport || !residentsDetails || !carers || !logs) return null

    return _(roundsReport)
      .map((round) => {
        if (!round.deleted || (round.residents && round.residents.length)) {
          return {
            ...round,
            residents: getRoundResidents({
              roundResidents: round.residents,
              carers,
              residentsDetails,
              residentSortByRoomNumber,
              logs
            })
          }
        }
        return null
      })
      .filter((d) => d)
      .value()
  }
)

const getCurrentDate = (state, { date }) => date || dayjs()

export const getMedicationRoundsSummary = createSelector(
  [getCurrentDate, getEMARRoundsReport],
  (currentDate, rounds) => {
    if (!rounds || !rounds.length) return null

    const filteredRounds = rounds.filter((round) => {
      const {
        deleted,
        PRN,
        scheduled: { due, signed, notAdministered }
      } = round

      return !deleted || PRN || due || signed || notAdministered
    })

    const latestRoundTime = getLatestRoundTime({
      currentDate,
      rounds: filteredRounds
    })

    const index = _.findIndex(filteredRounds, { time: latestRoundTime })
    let currentRounds
    if (index < 2) {
      currentRounds = _.take(filteredRounds, 3)
    } else if (index + 1 === filteredRounds.length) {
      // ie. is the last round
      currentRounds = filteredRounds.slice(index - 2, index + 1)
    } else {
      currentRounds = filteredRounds.slice(index - 1, index + 2)
    }

    return _.map(currentRounds, (round, index) => ({
      ...round,
      roundCount: currentRounds.length,
      allSigned: round.scheduled.due === 0 && round.scheduled.signed > 0,
      isDue: !isRoundOverdue({
        round,
        latestRoundTime,
        currentDate,
        today: currentDate
      })
    }))
  }
)
